<template>
  <div class="layout">
    <el-container style="height: 100%;overflow: hidden;">
      <el-aside class="aside" :width="isCollapse ? 'auto' : '240px'">
        <template v-if="env === 'sxsw'">
          <div style="width: 22px;height: 64px;"></div>
        </template>
        <template v-else>
          <div class="brand-logo" :class="{ collapsed: isCollapse }">
            <img
              v-if="isCollapse"
              :src="brandFavicon"
              :alt="brandName"
              class="brand-icon"
            />
            <img
              v-else
              :src="brandBanner"
              :alt="brandName"
              class="brand-banner"
            />
          </div>
        </template>
        <el-menu
          :default-active="activeMenu"
          :collapse="isCollapse"
          @select="handleMenuSelect">
          <template v-for="item in menu" :key="item.id">
            <el-sub-menu v-if="item.type === '1' && item.showFlag" :index="item.id">
              <template #title>
                <el-icon v-if="item.icon">
                  <component :is="item.icon"></component>
                </el-icon>
                <span>{{ item.name }}</span>
              </template>
              <template v-for="value in item.children">
                <el-menu-item
                  v-if="menuVisible(value)"
                  :key="value.id"
                  :index="value.href">
                  <el-icon v-if="value.icon">
                    <component :is="value.icon"></component>
                  </el-icon>
                  <template #title>
                    <span>{{value.name}}</span>
                    <el-badge v-if="value.href === '/research/inbox' && inboxStore.unreadCount > 0"
                      :value="inboxStore.unreadCount" :max="99" class="menu-inbox-badge" />
                  </template>
                </el-menu-item>
              </template>
            </el-sub-menu>

            <el-menu-item v-if="item.type === '2' && menuVisible(item)" :index="item.href">
              <el-icon v-if="item.icon">
                <component :is="item.icon"></component>
              </el-icon>
              <template #title>{{item.name}}</template>
            </el-menu-item>
          </template>
        </el-menu>
      </el-aside>
      <el-container>
        <el-header style="background-color: var(--imles-chrome-bg, #fff);height: 48px;padding: 0px;">
          <Header show-proect v-model="isCollapse"></Header>
        </el-header>
        <el-main>
          <router-view></router-view>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue"
import { useRouter, useRoute } from "vue-router"
import { useMenuStore, MENU_LOCALE_KEY } from "@/store/menu"
import { useCommonStore, useProjectStore } from "@/store/common"
import { useProjectInfoStore } from "@/store/project"
import { useInboxStore } from "@/store/inbox"
import { getMenuList } from "@/api/common"
import { getCurrentLocale } from "@/i18n"

const menuStore = useMenuStore()
const commonStore = useCommonStore()
const projectStore = useProjectStore()
const projectInfoStore = useProjectInfoStore()
const inboxStore = useInboxStore()
menuStore.getMenu();

// The sidebar menu tree is localised by the backend (sys_menu.name_en_hk /
// name_zh_hk) and cached in localStorage when a project is opened. A language
// switch reloads the page but does NOT re-fetch the menu, so its labels would
// otherwise stay in the locale active when the project was opened. Re-fetch
// under the current locale whenever the cached menu's locale no longer matches.
const cachedMenuLocale = window.localStorage.getItem(MENU_LOCALE_KEY);
if (projectStore.projectId && cachedMenuLocale !== getCurrentLocale()) {
  getMenuList({ projectId: projectStore.projectId }).then((res: any) => {
    if (res?.data?.menus) {
      menuStore.refreshMenu(res.data.menus);
    }
  }).catch(() => {});
}

const env = import.meta.env.VITE_APP_ENV;
const brand = (import.meta.env.VITE_BRAND || 'imles') as 'imles' | 'mycoach';
const brandName = brand === 'mycoach' ? 'Mycoach Health' : '医合易生';
const defaultFavicon = brand === 'mycoach'
  ? new URL('../assets/img/login/mycoach_favicon.png', import.meta.url).href
  : new URL('../assets/img/login/imles_favicon.ico', import.meta.url).href;
const defaultBanner = brand === 'mycoach'
  ? new URL('../assets/img/login/mycoach_logo.png', import.meta.url).href
  : new URL('../assets/img/login/imles_logo.png', import.meta.url).href;
// Once a project is opened, its uploaded logo pair replaces the default
// brand logos in the workbench sidebar — falls back to the brand logos
// when the project has no custom logos configured.
const brandFavicon = computed(
  () => projectInfoStore.projectInfo?.squareLogoUrl || defaultFavicon,
);
const brandBanner = computed(
  () => projectInfoStore.projectInfo?.companyLogoUrl || defaultBanner,
);

const router = useRouter();
const route = useRoute();
const activeMenu = ref("/home")
const isCollapse = computed({
  get () {
    return commonStore.isCollapse;
  },
  set () {
    commonStore.changeCollapse()
  }
});

const menu = computed(() => {
  return menuStore.menu;
})

// COCHE-only workbench pages are gated by the current project's per-feature flag.
// Existing (non-COCHE) projects leave these flags at 0, so the menu stays hidden
// for clients who have no such data; a project opts in by setting the flag to 1
// (e.g. cvd_hk). Map the menu's route -> the projectInfo flag that must be on.
const projectMenuGate: Record<string, () => boolean> = {
  "/manage/ingestion-hardening": () => !!projectInfoStore.projectInfo?.ingestionHardeningEnabled,
  "/manage/ehealth": () => !!projectInfoStore.projectInfo?.ehealthDctEnabled,
  "/manage/ehr-viewer": () => !!projectInfoStore.projectInfo?.ehealthDctEnabled,
};
const menuVisible = (m: any) => {
  if (!m?.showFlag) return false;
  const gate = projectMenuGate[m?.href];
  return gate ? gate() : true;
};

// 治疗回顾 unread badge: fetch once the menu carries the inbox entry and a
// project is open. Refreshed by the inbox store after mark-read — no polling.
const menuHasInbox = computed(() =>
  (menu.value || []).some((m: any) =>
    m?.href === "/research/inbox" ||
    (m?.children || []).some((c: any) => c?.href === "/research/inbox")));
watch(
  () => [menuHasInbox.value, projectInfoStore.projectInfo?.projectCode] as const,
  ([hasInbox, code]) => {
    if (!hasInbox) return;
    if (code) {
      inboxStore.fetchUnread(String(code));
    } else {
      projectInfoStore.fetchProjectInfo?.();
    }
  },
  { immediate: true },
);

const handleMenuSelect = (key: string) => {
  router.push({
    path: key
  })
}

watch(route, (route:any) => {
  activeMenu.value = route.path;
}, { immediate: true })
</script>

<style lang="scss" scoped>
.layout {
  height: 100%;
  background-color: #F5F7F9;
  overflow: hidden;
  .aside {
    height: 100%;
    background-color: #fff;
    border-right: 1px solid #DBDBDB;
    overflow-x: hidden;
  }
  .logo {
    display: flex;
    align-items: center;
    font-family: var(--imles-font-family-base);
    font-weight: 600;
    font-size: 22px;
    color: rgba(0,0,0,0.9);
    padding-left: 21px;
    padding-top: 17px;
    user-select: none;
    margin-bottom: 18px;
    &::before {
      display: block;
      content: "";
      width: 22px;
      height: 29px;
      background-image: url("../assets/logo.png");
      background-repeat: no-repeat;
      background-size: cover;
      margin-right: 17px;
    }
  }
  .brand-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 64px;
    user-select: none;
    margin-bottom: 18px;
    overflow: hidden;
    .brand-icon {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }
    .brand-banner {
      max-width: 200px;
      max-height: 56px;
      width: auto;
      height: auto;
      object-fit: contain;
    }
    &.collapsed {
      padding-left: 0;
      justify-content: center;
    }
  }
  .menu {
    list-style: none;
    margin: 0px;
    padding: 0px;
    font-family: var(--imles-font-family-base);
    font-weight: 500;
    font-size: 14px;
    color: #333333;
    line-height: 22px;
    text-align: left;
    font-style: normal;
    padding-top: 40px;
    li {
      user-select: none;
      cursor: pointer;
      position: relative;
      height: 40px;
      padding-left: 20px;
      display: flex;
      align-items: center;
      transition: all 0.2s ease;
      &::before {
        display: block;
        content: "";
        width: 13px;
        height: 13px;
        background-repeat: no-repeat;
        background-size: cover;
        margin-right: 13px;
      }
      &::after {
        display: block;
        content: "";
        width: 3px;
        height: 40px;
        position: absolute;
        right: 0px;
        top: 0px;
        background-color: #fff;
        transition: all 0.2s ease;
      }
    }
    li:hover {
      background-color: var(--imles-menu-hover-bg);
      color: var(--imles-primary);
      &::after {
        background-color: var(--imles-primary);
      }
    }
    li.active {
      background-color: var(--imles-menu-hover-bg);
      color: var(--imles-primary);
      &::after {
        background-color: var(--imles-primary);
      }
    }
    li:nth-child(1)::before {
      background-image: url("../assets/note.png");
    }
    li:nth-child(2)::before {
      background-image: url("../assets/template.png");
    }
  }
  .main-box {
    display: flex;
    gap: 20px;
    .main-left {
      width: 286px;
      flex-shrink: 1;
      .title {
        font-family: var(--imles-font-family-base);
        font-weight: 600;
        font-size: 20px;
        color: rgba(0,0,0,0.9);
        line-height: 28px;
        font-style: normal;
        margin: 0px;
        margin-bottom: 24px;
      }
      .avatar {
        width: 104px;
        height: 104px;
        border-radius: 50%;
        margin: 60px auto 32px;
        background-color: #eee;
        background-repeat: repeat;
        background-size: cover;
        background-image: url('../assets/head.png');
      }
      .user-info-item {
        font-family: var(--imles-font-family-base);
        font-weight: 500;
        font-size: 14px;
        color: #333333;
        line-height: 20px;
        text-align: center;
        font-style: normal;
        margin-bottom: 16px;
      }
      .tree-box {
        display: block;
      }
      .left-item {
        position: relative;
        padding: 28px 20px;
        height: 486px;
        background-color: #fff;
        border-radius: 8px;
        margin-bottom: 20px;
      }
    }
    .main-right {
      flex: 1;
      .title {
        font-family: var(--imles-font-family-base);
        font-weight: 600;
        font-size: 18px;
        color: rgba(0,0,0,0.9);
        line-height: 25px;
        text-align: left;
        font-style: normal;
        margin: 0px;
        padding-bottom: 17px;
        border-bottom: 1px solid #E1E1E1;
      }
      .btn-box {
        position: absolute;
        right: 28px;
        top: 18px;
      }
      .input-box {
        height: 380px;
        overflow-x: hidden;
        overflow-y: auto;
        padding-bottom: 20px;
      }
      .gen-box {
        box-sizing: border-box;
        width: 100%;
        height: 70px;
        line-height: 70px;
        background: #FFFFFF;
        box-shadow: 0px -6px 32px 5px rgba(0,0,0,0.04);
        border-radius: 0px 0px 8px 8px;
        position: absolute;
        bottom: 0px;
        left: 0px;
        text-align: center;
        padding: 0px 28px;
      }
      .right-item {
        position: relative;
        padding: 28px 26px;
        height: 486px;
        background-color: #fff;
        border-radius: 8px;
        margin-bottom: 20px;
      }
    }
  }
}
:deep(.create-btn) {
  position: absolute;
  top: 28px;
  right: 20px;
}
:deep(.el-textarea) {
  --el-input-focus-border: var(--imles-primary);
  --el-input-focus-border-color: var(--imles-primary);
}
:deep(.el-menu) {
  --el-menu-border-color: #fff;
}
.menu-inbox-badge {
  margin-left: 8px;
  vertical-align: middle;
  :deep(.el-badge__content) {
    border: none;
  }
}
</style>